import React from "react";
import { Card, CardContent } from "../components/ui/card";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/5 to-background py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="text-foreground/60">Last Updated: January 2025</p>
        </div>

        <Card className="bg-card/50 backdrop-blur-sm border-border/40">
          <CardContent className="p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Introduction</h2>
              <p className="text-foreground/70 leading-relaxed">
                Cogsmith ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our desktop 
                application and website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-foreground/90">2.1 Desktop Application</h3>
              <p className="text-foreground/70 leading-relaxed mb-4">
                The Cogsmith desktop application operates locally on your device. We do not collect, store, 
                or transmit any data from the desktop application to our servers. All data generated by the 
                application remains on your local device.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-foreground/90">2.2 Website</h3>
              <p className="text-foreground/70 leading-relaxed mb-2">
                When you visit our website or create an account, we may collect:
              </p>
              <ul className="list-disc list-inside text-foreground/70 space-y-2 ml-4">
                <li>Email address and name (for account creation)</li>
                <li>Purchase history and transaction information</li>
                <li>Browser type and version</li>
                <li>IP address and general location data</li>
                <li>Pages visited and time spent on our website</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. How We Use Your Information</h2>
              <p className="text-foreground/70 leading-relaxed mb-2">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-foreground/70 space-y-2 ml-4">
                <li>Process and fulfill your purchases</li>
                <li>Maintain your user account and purchase history</li>
                <li>Send you important updates about your account or purchases</li>
                <li>Improve our website and services</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Data Storage and Security</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                However, no method of transmission over the Internet or electronic storage is 100% secure.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Your website account information is stored securely on our servers. Desktop application 
                data is stored locally on your device and is not transmitted to our servers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Third-Party Services</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                We may use third-party service providers to help us operate our website and process 
                payments. These third parties have access to your personal information only to perform 
                specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                The desktop application may connect to third-party AI services (such as OpenAI) based on 
                your configuration. These connections are made directly from your device, and we do not 
                have access to the data transmitted to these services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Cookies</h2>
              <p className="text-foreground/70 leading-relaxed">
                Our website uses cookies to enhance your browsing experience, analyze site traffic, and 
                personalize content. You can control cookie settings through your browser preferences. 
                However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Your Rights</h2>
              <p className="text-foreground/70 leading-relaxed mb-2">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-foreground/70 space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your personal information</li>
                <li>Request restriction of processing your personal information</li>
                <li>Request transfer of your personal information</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Children's Privacy</h2>
              <p className="text-foreground/70 leading-relaxed">
                Our services are not intended for children under the age of 13. We do not knowingly 
                collect personal information from children under 13. If you are a parent or guardian and 
                believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Changes to This Privacy Policy</h2>
              <p className="text-foreground/70 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. You are 
                advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Contact Us</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-purple-950/20 border border-purple-500/20 rounded-lg p-4">
                <p className="text-foreground/80">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:info@cogsmith.ai" className="text-purple-400 hover:text-purple-300">
                    info@cogsmith.ai
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">11. Data Retention</h2>
              <p className="text-foreground/70 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes 
                outlined in this Privacy Policy, unless a longer retention period is required or permitted 
                by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">12. International Data Transfers</h2>
              <p className="text-foreground/70 leading-relaxed">
                Your information may be transferred to and maintained on servers located outside of your 
                state, province, country, or other governmental jurisdiction where data protection laws may 
                differ. By using our services, you consent to the transfer of your information to our 
                facilities and those third parties with whom we share it as described in this Privacy Policy.
              </p>
            </section>

            <section className="bg-purple-950/10 border border-purple-500/20 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-3 text-foreground">Important Note About Desktop Application</h2>
              <p className="text-foreground/70 leading-relaxed">
                The Cogsmith desktop application is designed with privacy in mind. All application data, 
                including your conversations, notes, and configurations, are stored locally on your device. 
                We do not have access to this data, and it is never transmitted to our servers. Your API 
                keys and credentials are stored securely on your local device using industry-standard 
                encryption methods.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;

